import React, { useState, useEffect, useMemo } from "react"

import { Button, ConfirmModal, RadioButtonsGroup } from "@cgtrader/triangle-ui/dist/src/ssr-index"
import {
  bool, func, arrayOf, shape, number, string,
} from "prop-types"

import { useIs3dPrintModel } from 'Pods/ItemPage/TopSection/hooks/useTopSectionProps'
import useFileProcessor from "Pods/Shared/Hooks/useFileProcessor"
import Sidebar from "Pods/Shared/Sidebar/Sidebar"

import useBreakpoint from "Utils/useBreakpoint"

import MediaContainer from "./Components/MediaContainer/MediaContainer"
import SidebarGalleryExtension from "./Components/SidebarGalleryExtension/SidebarGalleryExtension"
import UseCaseFormSection from "./Components/UseCaseFormSection/UseCaseFormSection"
import UseCasePermissionMessage from "./Components/UseCasePermissionMessage/UseCasePermissionMessage"
import useMediaHandling from "./hooks/useMediaHandling"
import useNotification from "./hooks/useNotification"
import usePermissions from "./hooks/usePermissions"
import useUseCaseActions from "./hooks/useUseCaseActions"
import useUseCaseForm from "./hooks/useUseCaseForm"
import useUseCaseTranslate from "./hooks/useUseCaseTranslate"
import { useCaseShape } from "./Utils/propTypes"
import { getUseCasesTabsOptions } from "./Utils/useCasesTabsOptions"

function UseCaseAreaSidebar({
  productId,
  currentUsername = '',
  isOpen,
  onAdd: handleAdd,
  onCloseSidebar: handleCloseSidebar,
  onRemove: handleRemove,
  onUpdate: handleUpdate,
  useCases,
  refetchCurrentMakes = null,
  initialSelectedUseCase = null,
  initialSelectedMediaIndex = 0,
  shouldOpenGalleryOnMount = false,
  canDownloadSlicerFile = true,
  showMediaContainer = true,
}) {
  const is3dPrintModel = useIs3dPrintModel()
  const { t, tv } = useUseCaseTranslate()
  const tabsOptions = useMemo(() => getUseCasesTabsOptions(t), [t])
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [useCaseToDelete, setUseCaseToDelete] = useState(null)
  const [printerTypeOptions, setPrinterTypeOptions] = useState([])
  const [isFetchingPrinterTypes, setIsFetchingPrinterTypes] = useState(false)
  const [hasRequestedPrinterTypes, setHasRequestedPrinterTypes] = useState(false)
  const [isGalleryOpen, setIsGalleryOpen] = useState(false)
  const [selectedUseCase, setSelectedUseCase] = useState(null)
  const [selectedMediaIndex, setSelectedMediaIndex] = useState(0)
  const [mediaContainerIndexes, setMediaContainerIndexes] = useState({})
  const [activeTab, setActiveTab] = useState('recent')
  const [editingUseCaseId, setEditingUseCaseId] = useState(null)

  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm'

  const { processFiles, isProcessing: isProcessingMedia, progress } = useFileProcessor()
  const { notification, showNotification, hideNotification } = useNotification()
  const {
    canDeleteUseCase,
    canEditUseCase,
    canUseLikeFeature,
    isBuyerWithAnUseCase,
    userHavePermissions,
    isFreeModel,
    isDownloaded,
  } = usePermissions({
    useCases,
    currentUsername,
  })

  const {
    control,
    pendingMedia,
    slicerFile,
    buildFormData,
    getFormValues,
    updatePendingMedia,
    handleSlicerFileSelect,
    handleSlicerFileRemove,
    resetForm,
    hasUnsavedChanges,
    canSubmit,
  } = useUseCaseForm({
    isProcessingMedia,
  })

  const {
    handleFilesAdded,
    handleEmbedAdded,
    handleRemoveMedia,
    handleReorderMedia,
    clearPendingMedia,
  } = useMediaHandling({
    processFiles,
    showNotification,
    pendingMedia,
    updatePendingMedia,
  })

  /**
   * Closes the sidebar and resets all related state:
   * - Resets printer types request flag (conditionally)
   * - Clears media container indexes
   * - Resets editing state
   * - Closes the sidebar UI (last, as it may trigger unmount)
   */
  const closeSidebarAndResetState = () => {
    resetPrinterTypesRequestState()
    setMediaContainerIndexes({})
    setEditingUseCaseId(null)
    handleCloseSidebar()
  }

  /**
   * Callback invoked after a use case is successfully created.
   * Closes the sidebar immediately for all roles. Success notifications are
   * shown as global (page-level) notifications that persist after the sidebar
   * closes, so users can still see their submission status.
   * Also ensures form state is completely reset.
   */
  const handleUseCaseCreationSuccess = () => {
    setShowCloseConfirmation(false)
    resetForm()
    closeSidebarAndResetState()

    if (refetchCurrentMakes) {
      refetchCurrentMakes()
    }
  }

  const {
    handleMediaPost,
    handleLike,
    handleUnlike,
    handleUseCaseApproval,
    handleUseCaseDeletion,
    handleUseCaseRejection,
    handleUseCaseUpdate,
    fetchPrinterTypes,
    isUpdating,
  } = useUseCaseActions({
    useCases,
    currentUsername,
    showLocalNotification: showNotification,
    clearPendingMedia,
    buildFormData,
    getFormValues,
    resetForm,
    handleAdd,
    handleRemove,
    handleUpdate,
    onCreateSuccess: handleUseCaseCreationSuccess,
    productId,
  })

  const canUserPost = userHavePermissions && canSubmit

  useEffect(() => {
    if (shouldOpenGalleryOnMount && initialSelectedUseCase && isOpen && !isMobile) {
      setMediaContainerIndexes({
        [initialSelectedUseCase.id]: initialSelectedMediaIndex ?? 0,
      })

      setSelectedUseCase(initialSelectedUseCase)
      setSelectedMediaIndex(initialSelectedMediaIndex ?? 0)
      setIsGalleryOpen(true)
    }
  }, [shouldOpenGalleryOnMount, initialSelectedUseCase, initialSelectedMediaIndex, isOpen, isMobile])

  useEffect(() => {
    if (selectedUseCase && useCases) {
      const updatedUseCase = useCases.find(uc => uc.id === selectedUseCase.id)
      if (updatedUseCase) {
        setSelectedUseCase(updatedUseCase)
      }
    }
  }, [useCases, selectedUseCase?.id])

  useEffect(() => {
    if (isMobile && isGalleryOpen) {
      setIsGalleryOpen(false)
    }
  }, [isMobile, isGalleryOpen])

  const resetPrinterTypesRequestState = () => {
    if (printerTypeOptions.length === 0) {
      setHasRequestedPrinterTypes(false)
    }
  }

  useEffect(() => {
    if (!is3dPrintModel || !isOpen || hasRequestedPrinterTypes) {
      return
    }

    setHasRequestedPrinterTypes(true)
    setIsFetchingPrinterTypes(true)

    fetchPrinterTypes({
      onSuccess: ({ data }) => {
        const options = (data?.printer_types || []).map((printerType) => ({
          label: printerType.name,
          value: printerType.id,
          printerTypeId: printerType.id,
          printerTypeName: printerType.name,
          technology: printerType.technology ? printerType.technology.toLowerCase() : undefined,
        }))

        setPrinterTypeOptions(options)
        setIsFetchingPrinterTypes(false)
      },
      onError: () => {
        setIsFetchingPrinterTypes(false)
        showNotification(t('errors.printerTypes'), 'error')
      },
    })
  }, [
    fetchPrinterTypes,
    is3dPrintModel,
    isOpen,
    hasRequestedPrinterTypes,
    showNotification,
    t,
  ])

  const handleInternalSidebarClose = () => {
    if (hasUnsavedChanges) {
      setShowCloseConfirmation(true)
      return
    }

    closeSidebarAndResetState()
  }

  const handleCloseConfirmation = () => {
    setShowCloseConfirmation(false)
    closeSidebarAndResetState()
  }

  const handleDeleteClick = (useCaseId) => {
    setUseCaseToDelete(useCaseId)
    setShowDeleteConfirmation(true)
  }

  const handleDeleteConfirmation = () => {
    if (useCaseToDelete) {
      handleUseCaseDeletion(useCaseToDelete)

      if (selectedUseCase && selectedUseCase.id === useCaseToDelete) {
        setIsGalleryOpen(false)
        setSelectedUseCase(null)
        setSelectedMediaIndex(0)
      }
    }
    setShowDeleteConfirmation(false)
    setUseCaseToDelete(null)
  }

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false)
    setUseCaseToDelete(null)
  }

  const handleUseCaseClick = (useCase, currentIndex) => {
    if (isMobile) return

    // If gallery is open and clicking the same use case, close it
    if (isGalleryOpen && selectedUseCase?.id === useCase.id) {
      handleCloseGallery()
      return
    }

    // Otherwise, open or switch to the clicked use case
    setSelectedUseCase(useCase)
    setSelectedMediaIndex(currentIndex)
    setIsGalleryOpen(true)
  }

  const handleCloseGallery = () => {
    setIsGalleryOpen(false)
    setSelectedUseCase(null)
    setSelectedMediaIndex(0)
  }

  const handleEditingChange = (useCaseId) => {
    setEditingUseCaseId(useCaseId)
  }

  const sortedUseCases = useMemo(() => {
    if (useCases.length === 0) return useCases

    return [...useCases].sort((a, b) => {
      if (activeTab === 'popular') {
        return b.likeCount - a.likeCount
      }
      return new Date(b.createdAt) - new Date(a.createdAt)
    })
  }, [useCases, activeTab])

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        closeSidebar={handleInternalSidebarClose}
        title={tv('area.title')}
        className="use-cases-sidebar"
      >
        {showCloseConfirmation && (
          <ConfirmModal
            header={t('sidebar.unsavedChanges')}
            submitText={t('sidebar.yes')}
            cancelText={t('sidebar.no')}
            onSubmit={handleCloseConfirmation}
            onCancel={() => setShowCloseConfirmation(false)}
            hideIcon
          />
        )}
        {showDeleteConfirmation && (
          <ConfirmModal
            header={tv('sidebar.deleteConfirm')}
            submitText={t('sidebar.delete')}
            cancelText={t('sidebar.cancel')}
            onSubmit={handleDeleteConfirmation}
            onCancel={handleDeleteCancel}
          />
        )}
        <div className="use-cases-area__scrollable">
          <div className="use-cases-area__top-container">
            {userHavePermissions && !editingUseCaseId && (
              <UseCaseFormSection
                control={control}
                pendingMedia={pendingMedia}
                slicerFile={slicerFile}
                handleFilesAdded={handleFilesAdded}
                handleEmbedAdded={handleEmbedAdded}
                handleRemoveMedia={handleRemoveMedia}
                handleReorderMedia={handleReorderMedia}
                handleSlicerFileSelect={handleSlicerFileSelect}
                handleSlicerFileRemove={handleSlicerFileRemove}
                printerTypeOptions={printerTypeOptions}
                isFetchingPrinterTypes={isFetchingPrinterTypes}
                useCases={useCases}
                isProcessing={isProcessingMedia}
                progress={progress}
                notification={notification}
                showNotification={showNotification}
                userHavePermissions={userHavePermissions}
                hideNotification={hideNotification}
              />
            )}

            {!userHavePermissions && (
              <UseCasePermissionMessage
                isBuyerWithAnUseCase={isBuyerWithAnUseCase}
                isFreeModel={isFreeModel}
                isDownloaded={isDownloaded}
              />
            )}
          </div>

          {showMediaContainer && useCases.length > 0 && (
            <div className="use-cases-area__tabs-container">
              <RadioButtonsGroup
                options={tabsOptions}
                name="use-cases-sort"
                value={activeTab}
                onChange={setActiveTab}
                wrapperClass="use-cases-area__radio-group"
                color="use-cases-area__tab-button-active"
              />
            </div>
          )}

          {showMediaContainer && (
            <MediaContainer
              canDeleteUseCase={canDeleteUseCase}
              canEditUseCase={canEditUseCase}
              canUseLikeFeature={canUseLikeFeature}
              isFetchingPrinterTypes={isFetchingPrinterTypes}
              onApprove={handleUseCaseApproval}
              onDelete={handleDeleteClick}
              onEditUseCase={handleUseCaseUpdate}
              onLike={handleLike}
              onReject={handleUseCaseRejection}
              onUnlike={handleUnlike}
              onUseCaseClick={handleUseCaseClick}
              printerTypeOptions={printerTypeOptions}
              useCases={sortedUseCases}
              userIsAllowedToModerate={userHavePermissions}
              isGalleryView={false}
              mediaContainerIndexes={mediaContainerIndexes}
              canDownloadSlicerFile={canDownloadSlicerFile}
              currentUsername={currentUsername}
              onShowNotification={showNotification}
              isUpdating={isUpdating}
              onEditingChange={handleEditingChange}
            />
          )}

          {userHavePermissions && !editingUseCaseId && (
            <div className="use-cases-area__submit-container">
              <Button
                type="primary"
                disabled={!canUserPost}
                onClick={handleMediaPost}
                expand
              >
                {tv('sidebar.share')}
              </Button>
            </div>
          )}
        </div>
      </Sidebar>

      <SidebarGalleryExtension
        isOpen={isGalleryOpen}
        onClose={handleCloseGallery}
        onCloseSidebar={handleInternalSidebarClose}
        useCase={selectedUseCase}
        initialMediaIndex={selectedMediaIndex}
        canDeleteUseCase={canDeleteUseCase}
        canEditUseCase={canEditUseCase}
        canUseLikeFeature={canUseLikeFeature}
        isFetchingPrinterTypes={isFetchingPrinterTypes}
        onApprove={handleUseCaseApproval}
        onDelete={handleDeleteClick}
        onEditUseCase={handleUseCaseUpdate}
        onLike={handleLike}
        onReject={handleUseCaseRejection}
        onUnlike={handleUnlike}
        printerTypeOptions={printerTypeOptions}
        useCases={useCases}
        userIsAllowedToModerate={userHavePermissions}
        currentUsername={currentUsername}
        onShowNotification={showNotification}
        isUpdating={isUpdating}
      />
    </>
  )
}

UseCaseAreaSidebar.propTypes = {
  productId: number.isRequired,
  currentUsername: string,
  isOpen: bool.isRequired,
  onCloseSidebar: func.isRequired,
  onAdd: func.isRequired,
  onRemove: func.isRequired,
  onUpdate: func.isRequired,
  initialSelectedMediaIndex: number,
  shouldOpenGalleryOnMount: bool,
  useCases: arrayOf(useCaseShape).isRequired,
  refetchCurrentMakes: func,
  initialSelectedUseCase: shape({
    id: string.isRequired,
  }),
  canDownloadSlicerFile: bool,
  showMediaContainer: bool,
}

export default UseCaseAreaSidebar
